/* One premiere. The page an artist gets in exchange for letting us have it first.
 *
 * So the two things that page has to do, in order: say what the record is and
 * when it lands, and send people to where they can buy it. The buy link is not a
 * footnote here — it is the consideration. A station that debuts a record and
 * then makes the shop a small grey line at the bottom has taken something and
 * given back a page view.
 *
 * PLAY LOADS THE PERSISTENT PLAYER. There is no <audio> element on this page and
 * there must never be one: the element lives above the router in App.tsx, so
 * pressing play here and then walking to the artist's resident page does not cut
 * the music. `premiereAsPlayable` (types.ts) is the adaptation, and the comment
 * on it says why the clearance it asserts is `direct_licence` and not the
 * default.
 *
 * THE PAGE SAYS A DIFFERENT THING ON EACH SIDE OF THE RELEASE DATE, and this is
 * the D14 fact that matters most on this file. While the record is unreleased,
 * nothing of it has been published or made available anywhere, so the
 * performers' exclusive right is live, their direct consent is fully effective,
 * and no statutory remuneration claim exists — this is the cleanest thing the
 * station broadcasts. On the release date that changes: art. 7(2) WNR counts a
 * phonogram "made available to the public" and §78(1) UrhG counts one
 * "published OR lawfully made available online", so from that day the recording
 * is inside the remuneration system and the exclusive right we relied on is
 * gone. It does not become unlawful — we hold consent, and the first play
 * already happened — but the reasoning is a different one, and the page reflects
 * that by changing what it leads with: "out 14 September on Flytree" and press play
 * becomes "out now on Flytree" and go buy it. `is_unreleased` is the server's
 * answer to that question, never a date comparison here; see types.ts.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api, isAbortError } from '../api';
import {
  Artwork,
  Chip,
  ChipList,
  Countdown,
  ErrorNote,
  Section,
  Spinner,
  releaseLine,
  useNarrow,
} from '../components';
import { formatClock, formatFullDate, formatTime, isoDate, readTzMode, tzLabel } from '../format';
import { usePlayer } from '../player';
import { premiereAsPlayable, premiereCatalogueNo } from '../types';
import type { PremiereDetail } from '../types';

/* ------------------------------------------------------------------ styles */

const CRUMB: CSSProperties = {
  padding: '14px clamp(12px, 4vw, 28px)',
  color: 'var(--muted)',
  borderBottom: 'var(--bw-hair) solid var(--edge)',
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
};

const HERO: CSSProperties = {
  display: 'flex',
  gap: 'clamp(20px, 4vw, 44px)',
  padding: '32px clamp(12px, 4vw, 28px) 40px',
  borderBottom: 'var(--bw) solid var(--edge)',
  flexWrap: 'wrap',
};

const HERO_HEAD: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  flexGrow: 1,
  minWidth: 0,
  flexBasis: '320px',
};

const TITLE: CSSProperties = {
  fontSize: 'clamp(2.25rem, 6vw, 4.75rem)',
};

/* The artist sits in the same display face as the track, at about half the
   step: on a premiere neither line is the caption of the other. */
const ARTIST: CSSProperties = {
  display: 'block',
  fontSize: '0.52em',
  lineHeight: 0.92,
  marginBottom: '0.08em',
};

/* The release line. A structural left rule rather than a colour field — the
   sentence has to carry the state on its own words, because a page printed in
   greyscale or read by somebody who cannot see the accent still has to say whether
   the record is out. */
const RELEASE: CSSProperties = {
  borderLeft: 'var(--bw-on) solid var(--edge)',
  paddingLeft: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '7px',
};

const RELEASE_WORDS: CSSProperties = {
  fontSize: 'clamp(1.375rem, 3vw, 2.125rem)',
  lineHeight: 1.02,
  letterSpacing: '-.01em',
  fontVariationSettings: "'wdth' 96, 'wght' 800",
  textTransform: 'uppercase',
  overflowWrap: 'anywhere',
};

const ACTIONS: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  flexWrap: 'wrap',
  marginTop: '4px',
};

const META_CELL: CSSProperties = {
  borderRight: 'var(--bw-hair) solid var(--edge)',
  padding: '14px 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  minWidth: 0,
};

const META_VALUE: CSSProperties = {
  fontSize: '1.1875rem',
  lineHeight: 1.1,
  fontVariationSettings: "'wdth' 96, 'wght' 800",
  textTransform: 'uppercase',
  overflowWrap: 'anywhere',
};

const NOTICE: CSSProperties = {
  padding: '24px clamp(12px, 4vw, 28px) 28px',
  borderBottom: 'var(--bw) solid var(--edge)',
  background: 'var(--surface)',
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
};

const BODY_PAD: CSSProperties = {
  padding: '26px clamp(12px, 4vw, 28px) 34px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const TAIL: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  flexWrap: 'wrap',
  padding: '26px clamp(12px, 4vw, 28px) 40px',
};

/* --------------------------------------------------------------- fragments */

function paragraphs(text: string | null): string[] {
  if (!text) return [];
  return text
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
}

/**
 * `flytree.bandcamp.com` — where the buy button actually goes.
 *
 * Printed under the button because it is somebody else's shop and the visitor
 * should know which one before they leave the station. A URL we cannot parse
 * answers null and the line is dropped rather than guessed at.
 */
function shopHost(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, '') || null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------- page */

export default function Premiere() {
  const { slug } = useParams<{ slug: string }>();
  const [tz] = useState(readTzMode);
  const narrow = useNarrow(900);

  const [premiere, setPremiere] = useState<PremiereDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!slug) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setPremiere(null);

    api
      .getPremiere(slug, controller.signal)
      .then((found) => {
        setPremiere(found);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        if (isAbortError(failure)) return;
        setError(failure);
        setLoading(false);
      });

    return () => controller.abort();
  }, [slug, attempt]);

  useEffect(() => {
    document.title = premiere
      ? `${premiere.artist_name} — ${premiere.track_title} — Higher Ground Radio`
      : 'Premiere — Higher Ground Radio';
  }, [premiere]);

  /* -------------------------------------------------------------- player */

  const player = usePlayer();
  const playerRef = useRef(player);
  playerRef.current = player;

  /* MATCHED ON `audio_url`, NOT ON THE CATALOGUE NUMBER. A premiere is carried
     through the player under its RELEASE catalogue (types.ts), and an EP whose
     two tracks both premiere here shares one of those — the file is what is
     unique per track. */
  const isCurrent =
    premiere !== null &&
    premiere.audio_url !== null &&
    player.mode === 'episode' &&
    player.current?.audio_url === premiere.audio_url;

  const playThis = useCallback(() => {
    if (!premiere) return;
    if (isCurrent) {
      playerRef.current.toggle();
      return;
    }
    playerRef.current.playEpisode(premiereAsPlayable(premiere));
  }, [premiere, isCurrent]);

  const notes = useMemo(() => paragraphs(premiere?.blurb ?? null), [premiere?.blurb]);

  /* ---------------------------------------------------------------- view */

  if (loading) {
    return (
      <>
        <Section level={1} title="Premiere" meta="Loading" />
        <div style={{ padding: '34px clamp(12px, 4vw, 28px)' }}>
          <Spinner label="Loading this premiere" />
        </div>
      </>
    );
  }

  if (error || !premiere) {
    return (
      <>
        <Section level={1} title="Premiere" meta={(slug ?? '').toUpperCase()} />
        <ErrorNote error={error} what="this premiere" onRetry={() => setAttempt((n) => n + 1)} />
        <div
          style={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            padding: '0 clamp(12px, 4vw, 28px) 40px',
          }}
        >
          <Link className="btn" to="/premieres">
            All premieres
          </Link>
          <Link className="btn-ghost" to="/archive">
            Browse the archive
          </Link>
        </div>
      </>
    );
  }

  const catalogue = premiereCatalogueNo(premiere);
  const label = (premiere.label_name || '').trim();
  const host = shopHost(premiere.buy_url);

  /* WHICH BUTTON IS THE PRIMARY ONE, and it is not a style preference. While the
     record is unreleased this page is the only place it exists, so the primary
     action is to hear it. Once it is out, the record itself is the thing to have
     and the shop link is what the artist is owed for the first play — so the
     buy button takes the large field and play steps down to the ghost. Exactly
     one primary action per surface, either way. */
  const buyIsPrimary = !premiere.is_unreleased && Boolean(premiere.buy_url);

  /* No file, and it has not gone out yet: the audio goes up when it airs. Fails
     closed on an unparseable date — no countdown rather than a wrong one. */
  const parsedAir = Date.parse(premiere.premieres_at);
  const upcoming =
    !premiere.audio_url && Number.isFinite(parsedAir) && parsedAir > Date.now();

  const airDate = formatFullDate(premiere.premieres_at, tz);
  const airTime = formatTime(premiere.premieres_at, tz);

  return (
    <>
      <nav className="mono" style={CRUMB} aria-label="Breadcrumb">
        <Link to="/premieres">Premieres</Link>
        <span aria-hidden="true">→</span>
        <span aria-current="page">{catalogue}</span>
      </nav>

      <div style={HERO}>
        <div style={{ width: narrow ? '100%' : 'clamp(260px, 34vw, 460px)', flexShrink: 0 }}>
          {/* A palette field and type, never an <img> of supplied cover art —
              see components/Artwork.tsx. The label's own catalogue number is the
              face, which is the one number a listener can act on. */}
          <Artwork
            color={premiere.artwork_color}
            catalogueNo={catalogue}
            label="Premiere"
            size="hero"
          />
        </div>

        <div style={HERO_HEAD}>
          <ChipList label="This premiere">
            {premiere.is_unreleased ? (
              <Chip color="ink">Unreleased</Chip>
            ) : (
              <Chip>Out now</Chip>
            )}
            {premiere.host ? (
              <Chip to={`/residents/${encodeURIComponent(premiere.host.slug)}`}>
                Played by {premiere.host.name}
              </Chip>
            ) : null}
          </ChipList>

          <h1 className="t-mega" style={TITLE}>
            <span style={ARTIST}>{premiere.artist_name}</span>
            {premiere.track_title}
          </h1>

          <div style={RELEASE}>
            <div style={RELEASE_WORDS}>{releaseLine(premiere, tz, 'long')}</div>
            <div className="mono-13 dim">
              {catalogue} · {formatClock(premiere.duration_seconds)} ·{' '}
              {upcoming ? `Premieres ${airDate}` : `Premiered ${airDate}`}
            </div>
          </div>

          {premiere.genres.length > 0 ? (
            <ChipList label="Genres">
              {premiere.genres.map((genre) => (
                <Chip
                  key={genre.slug}
                  color={genre.color_token}
                  to={`/premieres?genre=${encodeURIComponent(genre.slug)}`}
                >
                  {genre.name}
                </Chip>
              ))}
            </ChipList>
          ) : null}

          <div style={ACTIONS}>
            {premiere.audio_url ? (
              <button
                type="button"
                className={buyIsPrimary ? 'btn-ghost' : 'btn-lg'}
                style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
                onClick={playThis}
                aria-pressed={isCurrent && player.isPlaying}
              >
                <span
                  className={isCurrent && player.isPlaying ? 'pausebars' : 'tri'}
                  aria-hidden="true"
                />
                {isCurrent && player.isPlaying
                  ? 'Pause'
                  : isCurrent
                    ? 'Resume'
                    : 'Play the premiere'}
              </button>
            ) : null}

            {/* THE LINK THE ARTIST IS DOING THIS FOR. A real anchor: it leaves
                the station, so it opens in a new tab and carries
                rel="noopener noreferrer" — no window handle back into this tab,
                and no Referer header telling somebody else's shop analytics
                which page a listener came from. */}
            {premiere.buy_url ? (
              <a
                className={buyIsPrimary ? 'btn-lg' : 'btn-ghost'}
                href={premiere.buy_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {premiere.is_unreleased ? 'Pre-order it' : 'Buy it'} ↗
              </a>
            ) : null}
          </div>

          {/* No dead button when there is no shop link: a line that says what is
              missing and whose it is to fill in. */}
          {premiere.buy_url ? (
            host ? (
              <p className="mono dim" style={{ margin: 0, lineHeight: 1.6 }}>
                Opens {host} in a new tab
              </p>
            ) : null
          ) : (
            <p className="mono dim" style={{ margin: 0, lineHeight: 1.6 }}>
              {premiere.is_unreleased
                ? `No pre-order link yet. It goes here the day ${label || 'the label'} opens one.`
                : `We have no shop link for this one. It is out on ${label || 'the label'}.`}
            </p>
          )}

          {/* The editorial half of the licence fact above the file header. Said
              plainly, because for the artist reading this page it is the offer,
              and for a listener it is the reason this section exists. */}
          <p className="t-body" style={{ fontSize: '0.9375rem' }}>
            {premiere.is_unreleased
              ? 'First play. This record is not out anywhere yet, so the only place to hear it is here — and the only permission behind it is the artists’ own.'
              : 'It is out now. The premiere ran before the release, so this page is the record of the first play; the shop link is the artist’s.'}
          </p>

          {premiere.slot ? (
            <p className="mono dim" style={{ margin: 0, lineHeight: 1.7 }}>
              First played in{' '}
              <Link to={`/schedule/${isoDate(premiere.slot.starts_at, 'station')}`}>
                {premiere.slot.title}
              </Link>{' '}
              · {premiere.slot.catalogue_no} · {formatTime(premiere.slot.starts_at, tz)}
            </p>
          ) : null}
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: narrow ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
          borderBottom: 'var(--bw) solid var(--edge)',
        }}
      >
        <div style={META_CELL}>
          <div className="mono dim">Release</div>
          <div style={META_VALUE}>
            {premiere.release_date && premiere.release_date.trim()
              ? formatFullDate(premiere.release_date, tz)
              : 'Not dated yet'}
          </div>
          <div className="mono dim">{premiere.is_unreleased ? 'Unreleased' : 'Out now'}</div>
        </div>
        <div style={META_CELL}>
          <div className="mono dim">Label</div>
          <div style={META_VALUE}>{label || 'Self-released'}</div>
          <div className="mono dim">{catalogue}</div>
        </div>
        <div style={META_CELL}>
          <div className="mono dim">{upcoming ? 'Premieres' : 'Premiered'}</div>
          <div style={META_VALUE}>
            {airDate} · {airTime}
          </div>
          <div className="mono dim">{tzLabel(tz, premiere.premieres_at)}</div>
        </div>
        <div style={{ ...META_CELL, borderRight: 0 }}>
          <div className="mono dim">Runtime</div>
          <div style={META_VALUE}>{formatClock(premiere.duration_seconds)}</div>
          <div className="mono dim">
            {premiere.host ? premiere.host.name : 'Station premiere'}
          </div>
        </div>
      </div>

      {/* Not yet on air. A countdown rather than a disabled play button, and it
          refetches when it lands, so a tab left open through the premiere gets
          the file without anybody reloading. */}
      {upcoming ? (
        <div style={NOTICE}>
          <div className="mono dim">On air in</div>
          <Countdown
            to={premiere.premieres_at}
            elapsedLabel="On air now"
            description={`${premiere.track_title} premieres at ${airTime} on ${airDate}, ${tzLabel(tz, premiere.premieres_at)}.`}
            onElapsed={() => setAttempt((n) => n + 1)}
          />
          <p className="t-body">
            It goes out on the stream first. The file lands on this page when the show does — until
            then there is nothing to press, and that is the point of a premiere.
          </p>
        </div>
      ) : !premiere.audio_url ? (
        /* Aired, but we hold no file. Stated as the fact it is: the premiere
           happened, the record is the record, and the buy link still stands. */
        <div style={NOTICE}>
          <div className="mono dim">Not on the server</div>
          <p className="t-body">
            This one went out on the stream and we do not keep a copy of it here. The release
            details stay on this page{premiere.buy_url ? ', and so does the link to buy it' : ''}.
          </p>
        </div>
      ) : null}

      {notes.length > 0 ? (
        <>
          <Section title="About this record" meta={premiere.artist_name} />
          <div style={BODY_PAD}>
            {notes.map((block, index) => (
              <p className="t-body" key={index}>
                {block}
              </p>
            ))}
          </div>
        </>
      ) : null}

      <div style={TAIL}>
        <Link className="btn-ghost" to="/premieres">
          All premieres
        </Link>
        {premiere.host ? (
          <Link className="btn-ghost" to={`/residents/${encodeURIComponent(premiere.host.slug)}`}>
            {premiere.host.name}
          </Link>
        ) : null}
        <span className="mono dim">Streamed here, sold by the artist. No downloads from us.</span>
      </div>
    </>
  );
}
